import invariant from "ts-invariant";
import React from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import {
  CommandOption,
  CommandTriggerConfigs,
  TriggerConfigs,
} from "@dots-projects/types";
import { useTriggerEditorStateDispatch } from "./TriggerEditor";
import { RootFormWithCommonParts } from "./RootFormWithCommonParts";
import { FormLabel } from "./FormLabel";
import { TriggerRootFormProps } from "./TriggerRootForm";
import { Input } from "./Input";
import { Checkbox } from "antd";
import { CloseCircleFilled } from "@ant-design/icons";
import { nanoid } from "nanoid";
import { saveDraft, updateTriggerConfig } from "./reducer/actionCreators";

const MAX_COMMAND_OPTIONS = 5;

type TriggerRootFormButtonClickProps = TriggerRootFormProps;

export default function TriggerRootFormCommand({
  trigger,
}: TriggerRootFormButtonClickProps) {
  invariant(trigger.type === "command");

  const {
    commandName,
    description,
    ephemeral,
    responseOnStart,
    responseOnComplete,
    options,
    skipDefer,
  } = trigger.configs as CommandTriggerConfigs;

  const defaultValues = {
    name: commandName || "test-command",
    description: description || "My test's command description",
    ephemeral: ephemeral ?? true,
    responseOnStart: responseOnStart || "My test's command response on start",
    responseOnComplete: responseOnComplete || "Success!",
    options: (options as CommandOption[]) || ([] as CommandOption[]),
  };

  const form = useForm({
    defaultValues,
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const {
    handleSubmit,
    control,
    register,
    setValue,
    formState: { errors },
  } = form;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "options", // unique name for your Field Array
    //I did this because otherwise react-form will add custom keys
    keyName: "key" as "id",
  });

  const { state, dispatch, onPersist } = useTriggerEditorStateDispatch();
  const readOnly = !state.trigger.current.isDraft;
  const isPersisting = state.editor.isPersisting === "save-draft";

  const onSubmit = (data: any) => {
    const commandTriggerConfigs = {
      ...trigger.configs,
      commandName: data.name || defaultValues.name,
      description: data.description || defaultValues.description,
      ephemeral: data.ephemeral ?? defaultValues.ephemeral,
      responseOnStart: data.responseOnStart || defaultValues.responseOnStart,
      responseOnComplete:
        data.responseOnComplete || defaultValues.responseOnComplete,
      options: data.options || defaultValues.options,
      skipDefer,
    };

    const triggerConfig = {
      type: "command",
      configs: commandTriggerConfigs,
    };

    dispatch(
      updateTriggerConfig(
        triggerConfig as TriggerConfigs,
        triggerConfig as TriggerConfigs
      )
    );
    dispatch(saveDraft(onPersist));
  };

  return (
    <RootFormWithCommonParts
      onSubmit={handleSubmit(onSubmit)}
      form={form}
      trigger={trigger}
    >
      {/*Take a look to https://discord.com/developers/docs/interactions/application-commands#application-command-object-application-command-naming*/}
      <FormLabel label="Command name ">
        <div style={{ width: "100%" }}>
          <Input
            style={{ width: "100%" }}
            {...register("name")}
            maxLength={27}
            onChange={(e) => {
              // Replacing white spaces with dashes and removing special characters
              const cleanName = e.target.value
                .replace(/\s/, "-")
                .replace(/[^a-z\d-]/gi, "")
                .toLowerCase();
              setValue("name", cleanName);
            }}
          />
        </div>
      </FormLabel>

      <FormLabel label="Command description ">
        <div style={{ width: "100%" }}>
          <Input
            maxLength={100}
            style={{ width: "100%" }}
            {...register("description")}
          />
        </div>
      </FormLabel>

      <FormLabel label="Response on start" />
      <div style={{ width: "100%" }}>
        <Input style={{ width: "100%" }} {...register("responseOnStart")} />
      </div>

      <FormLabel label="Response on complete" />
      <div style={{ width: "100%" }}>
        <Input style={{ width: "100%" }} {...register("responseOnComplete")} />
      </div>

      {/*Ephemeral*/}
      <Controller
        control={control}
        name="ephemeral"
        render={({ field }) => (
          <>
            <Checkbox
              disabled={readOnly}
              checked={field.value}
              onChange={field.onChange}
            >
              <span className="text-gray-500">Only visible to the user</span>
            </Checkbox>
          </>
        )}
      />

      <FormLabel label="Options" />
      {fields.map((f, idx) => (
        <div key={f.id} className="w-full">
          <div
            className="flex relative"
            onClick={(event) => {
              //Preventing event bubbling
              event.preventDefault();
            }}
          >
            <div className="font-bold">Option {idx + 1}</div>

            <button
              className="absolute right-1"
              onClick={() => {
                remove(idx);
              }}
              disabled={isPersisting}
            >
              <CloseCircleFilled />
            </button>
          </div>

          <FormLabel label="Name" />
          <div className="flex w-full flex-row gap">
            <div className="basis-full">
              <Input
                style={{ width: "100%" }}
                {...register(`options.${idx}.name`, {
                  required: true,
                })}
                maxLength={32}
                onChange={(e) => {
                  const cleanOptionName = e.target.value
                    .replace(/\s/g, "-")
                    .replace(/[^a-z\d-]/gi, "")
                    .toLowerCase();
                  setValue(`options.${idx}.name`, cleanOptionName);
                }}
              />
              {errors.options?.[idx]?.["name"]?.["type"] === "required" && (
                <p className="text-red-500 m-0">Option name required!</p>
              )}
            </div>
          </div>

          <FormLabel label="Description" />
          <div className="flex w-full flex-row gap">
            <div className="basis-full">
              <Input
                style={{ width: "100%" }}
                {...register(`options.${idx}.description`, { required: true })}
              />
            </div>
          </div>

          <div className="flex w-full flex-row gap">
            <div className="basis-full">
              <Controller
                control={control}
                name={`options.${idx}.optional`}
                render={({ field }) => (
                  <>
                    <Checkbox
                      disabled={readOnly}
                      checked={!field.value}
                      onChange={(e) => {
                        field.onChange(!e.target.checked);
                      }}
                    >
                      <span className="text-gray-500">Required option</span>
                    </Checkbox>
                  </>
                )}
              />
            </div>
          </div>
        </div>
      ))}
      {!readOnly && (
        <button
          className={
            fields.length >= MAX_COMMAND_OPTIONS
              ? "opacity-50 cursor-not-allowed"
              : ""
          }
          disabled={fields.length >= MAX_COMMAND_OPTIONS}
          onClick={(event) => {
            event.preventDefault();
            append(
              {
                id: `${nanoid(6)}-option`,
                name: `test-option-${fields.length + 1}`,
                description: `${fields.length + 1} test option`,
                optional: false,
                type: "string", //TODO: Add type selector
              },
              { shouldFocus: true }
            );
          }}
        >
          Add Option
        </button>
      )}
    </RootFormWithCommonParts>
  );
}
